import {OnChangeFn} from "@tanstack/react-table";
import {FiTrash} from "react-icons/fi";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TwoLineShortDateTimeUser} from "@/components/datetime/TwoLineShortDateTimeUser.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {EllipsisText} from "@/components/typography/EllipsisText.tsx";
import {SubscriptionEmailDomain, SubscriptionEmailDomainType} from "@/lib/types/statuspages.ts";
import {DeleteEmailDomainsActionButton} from "@/pages/statuspages/tabs/subscribers/components/allow-block-list/actions/DeleteEmailDomainsActionButton.tsx";
import {EmailDomainsActionBar} from "@/pages/statuspages/tabs/subscribers/components/allow-block-list/actions/EmailDomainsActionBar.tsx";

import styles from "./AllowBlockListTable.module.scss";

export interface AllowBlockListTableProps {
  domainType: SubscriptionEmailDomainType;
  items: SubscriptionEmailDomain[];
  rowSelection: Record<string, boolean>;
  setRowSelection: OnChangeFn<Record<string, boolean>>;
}

export const AllowBlockListTable = ({domainType, items, rowSelection, setRowSelection}: AllowBlockListTableProps) => {
  const {user} = useCurrentUserContext();
  const selectedDomainIds = Object.keys(rowSelection)
    .map((idx) => items[parseInt(idx)]?.id)
    .filter((domainId) => domainId);
  const specs: ColumnSpec<SubscriptionEmailDomain>[] = [
    {
      fieldName: "domain",
      fieldTitle: "Domain",
      meta: {
        headerClassName: styles.domainColumn,
        columnClassName: styles.domainColumn,
      },
      accessor: (obj: SubscriptionEmailDomain) => (
        <EllipsisText className={"d-block text-nowrap"} toolTip={obj.domain}>
          {obj.domain}
        </EllipsisText>
      ),
    },
    {
      fieldName: "created_at",
      fieldTitle: "Date Added",
      accessor: (obj: SubscriptionEmailDomain) => (
        <TwoLineShortDateTimeUser date={obj.created_at.toString()} user={user!} />
      ),
    },
  ];
  if (user?.has_write_access) {
    specs.push({
      fieldName: "action",
      accessor: (obj: SubscriptionEmailDomain) => (
        <DeleteEmailDomainsActionButton
          domainIds={[obj.id]}
          onSuccess={() => {}}
          isBulkAction={false}
          domainType={domainType}
          variant="tableRow"
        >
          <Icon icon={FiTrash} />
        </DeleteEmailDomainsActionButton>
      ),
      meta: {
        headerClassName: styles.actionColumn,
        columnClassName: styles.actionColumn,
      },
    });
  }
  return (
    <Table
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      rows={items}
      specs={specs}
      initialState={{columnPinning: {right: ["action"]}}}
      actionBar={
        user?.has_write_access && (
          <EmailDomainsActionBar
            domainType={domainType}
            domainIds={selectedDomainIds}
            onDeleteSuccess={() => setRowSelection({})}
          />
        )
      }
      noDataComponent={
        <EmptyTable colSpan={3}>
          This Status Page does not have any{" "}
          {domainType === SubscriptionEmailDomainType.ALLOWED ? "Allowed" : "Blocked"} Email Domains.
        </EmptyTable>
      }
    />
  );
};
